import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { tasksService } from '../api/tasks.service';
import { TASK_KEYS } from './queryKeys';
import { updateTaskEntity } from './useTaskEntityCache';
import type { TaskDetail } from '../types';
import type { Tag } from '@/features/tags/public';

/**
 * Resolves a task's assigned tags from its embedded `tagIds` (present on both
 * the list payload and the task detail payload) against the project's tag
 * catalog — no per-task network request, same pattern as status/assignee.
 */
export function resolveTaskTags(tagIds: string[] | undefined, allProjectTags: Tag[]): Tag[] {
  if (!tagIds || tagIds.length === 0) return [];
  const idSet = new Set(tagIds);
  return allProjectTags.filter((t) => t.id != null && idSet.has(t.id));
}

export function useAttachTag(taskId: string) {
  const queryClient = useQueryClient();
  const activeOrganizationId = useOrganizationStore((s) => s.activeOrganizationId)!;

  return useMutation({
    mutationFn: (tag: Tag) => tasksService.attachTag(taskId, { tagId: tag.id ?? '' }),

    onMutate: async (tag) => {
      const detailKey = TASK_KEYS.detail(activeOrganizationId, taskId);
      await queryClient.cancelQueries({ queryKey: detailKey });

      const previousDetail = queryClient.getQueryData<TaskDetail>(detailKey);
      if (previousDetail && !previousDetail.tags.some((t) => t.id === tag.id)) {
        queryClient.setQueryData<TaskDetail>(detailKey, {
          ...previousDetail,
          tags: [...previousDetail.tags, tag],
          tagIds: [...(previousDetail.tagIds ?? []), tag.id ?? ''],
        });
      }

      updateTaskEntity(queryClient, activeOrganizationId, taskId, (task) => {
        const tagIds = task.tagIds ?? [];
        return tagIds.includes(tag.id ?? '')
          ? task
          : { ...task, tagIds: [...tagIds, tag.id ?? ''] };
      });

      return { previousDetail };
    },

    onError: (_err, _vars, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(
          TASK_KEYS.detail(activeOrganizationId, taskId),
          context.previousDetail,
        );
        updateTaskEntity(queryClient, activeOrganizationId, taskId, () => context.previousDetail!);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.detail(activeOrganizationId, taskId) });
    },
  });
}

export function useDetachTag(taskId: string) {
  const queryClient = useQueryClient();
  const activeOrganizationId = useOrganizationStore((s) => s.activeOrganizationId)!;

  return useMutation({
    mutationFn: (tagId: string) => tasksService.detachTag(taskId, tagId),

    onMutate: async (tagId) => {
      const detailKey = TASK_KEYS.detail(activeOrganizationId, taskId);
      await queryClient.cancelQueries({ queryKey: detailKey });

      const previousDetail = queryClient.getQueryData<TaskDetail>(detailKey);
      if (previousDetail) {
        queryClient.setQueryData<TaskDetail>(detailKey, {
          ...previousDetail,
          tags: previousDetail.tags.filter((t) => t.id !== tagId),
          tagIds: (previousDetail.tagIds ?? []).filter((id) => id !== tagId),
        });
      }

      updateTaskEntity(queryClient, activeOrganizationId, taskId, (task) => ({
        ...task,
        tagIds: (task.tagIds ?? []).filter((id) => id !== tagId),
      }));

      return { previousDetail };
    },

    onError: (_err, _vars, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(
          TASK_KEYS.detail(activeOrganizationId, taskId),
          context.previousDetail,
        );
        updateTaskEntity(queryClient, activeOrganizationId, taskId, () => context.previousDetail!);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.detail(activeOrganizationId, taskId) });
    },
  });
}
